import React, { useEffect, useState } from "react";
import { fetchRiskEvents } from "../services/adminAuditApi";
import "../styles/Admin.css";

function formatDate(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

function severityClass(severity) {
  const s = (severity || "").toLowerCase();
  if (s === "critical") return "ucx-badge ucx-badge-critical";
  if (s === "high") return "ucx-badge ucx-badge-high";
  if (s === "warn" || s === "warning") return "ucx-badge ucx-badge-warn";
  return "ucx-badge ucx-badge-info";
}

function AdminRiskPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    severity: "",
    module: "",
  });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const data = await fetchRiskEvents({
          limit: 100,
          offset: 0,
          module: filters.module || undefined,
          severity: filters.severity || undefined,
        });

        if (!cancelled) {
          if (!data || data.ok !== true) {
            setError("Unable to load risk events.");
            setItems([]);
          } else {
            setItems(Array.isArray(data.items) ? data.items : []);
          }
        }
      } catch (err) {
        console.error("[AdminRiskPage] load error:", err);
        if (!cancelled) {
          setError("Unable to load risk events. Check your connection.");
          setItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [refreshKey, filters.module, filters.severity]);

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ severity: "", module: "" });
  };

  return (
    <div className="ucx-admin-page">
      <div className="ucx-admin-header">
        <div>
          <h1 className="ucx-admin-title">Risk Events</h1>
          <p className="ucx-admin-subtitle">
            SafeMatrix flags for suspicious or anomalous activity across UNI-CODEX.
          </p>
        </div>
        <div className="ucx-admin-header-right">
          <div className="ucx-admin-filters">
            <select
              name="severity"
              value={filters.severity}
              onChange={handleFilterChange}
            >
              <option value="">All severities</option>
              <option value="info">Info</option>
              <option value="warn">Warning</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <input
              type="text"
              name="module"
              value={filters.module}
              onChange={handleFilterChange}
              placeholder="Module (e.g. identity)"
            />
          </div>
          <div className="ucx-admin-header-actions">
            <button
              type="button"
              className="ucx-btn ucx-btn-outline"
              onClick={handleRefresh}
              disabled={loading}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="ucx-btn ucx-btn-outline ucx-admin-clear-filters"
              onClick={handleClearFilters}
              disabled={loading && !filters.severity && !filters.module}
            >
              Clear filters
            </button>
            <span className="ucx-small">
              Showing {items.length || 0} event(s)
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="ucx-admin-status ucx-admin-status-error">
          {error}
        </div>
      )}

      <div className="ucx-admin-table-wrapper">
        <table className="ucx-admin-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Severity</th>
              <th>Module</th>
              <th>Code</th>
              <th>Description</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="ucx-admin-empty">
                  No risk events found for the current filters.
                </td>
              </tr>
            )}

            {items.map((row) => (
              <tr key={row.id}>
                <td>{formatDate(row.createdAt)}</td>
                <td>
                  <span className={severityClass(row.severity)}>
                    {row.severity}
                  </span>
                </td>
                <td>{row.module}</td>
                <td>{row.code}</td>
                <td>{row.description}</td>
                <td>{row.status}</td>
              </tr>
            ))}

            {loading && (
              <tr>
                <td colSpan={6} className="ucx-admin-empty">
                  Loading risk events…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminRiskPage;
